/**
 * station_props.ts — the FURNITURE of an activity station (society wave,
 * 2026-09-19): benches, tavern tables with stools, the story hearth, the ration
 * table, a browsing stall, kneelers, the school mat, the well bucket, the butts,
 * the wrestling ring, the infirmary cots, the archive desks.
 *
 * Procedural on purpose: a seat is what makes a seated clip honest (the
 * never-mime law), so every station must have its prop on EVERY tier, the
 * moment it exists, with no asset to stream. One merged, vertex-coloured
 * geometry per prop kind (cached for the session) = one draw per station.
 *
 * FRAME: station-local, +Z is the way the station faces, y = 0 the ground. Seat
 * tops sit at `SEAT_H` and every seat is centred on its formation slot
 * (lib/activity_catalog.ts formationSlot) — the prop and the seating are cut
 * from the same numbers, so a sitter is always ON a seat.
 */
import * as THREE from 'three/webgpu';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { stationDef, formationSlot } from './activity_catalog';

/** Height of a seat top (the rig's chair-sit clips put the hips here). */
export const SEAT_H = 0.46;

const WOOD = 0x7a5a3a, WOOD_DARK = 0x4f3a26, ALLOY = 0x9aa3ad, STONE = 0x77736c, CLOTH = 0x6f8fa6, STRAW = 0xc9b26b, EMBER = 0xd9662b, ROPE = 0xb9a377, RED = 0xa8402e, WHITE = 0xe8e2d2;

function tinted(g: THREE.BufferGeometry, hex: number): THREE.BufferGeometry {
  const ng = g.index ? g.toNonIndexed() : g;
  const c = new THREE.Color(hex);
  const n = ng.getAttribute('position').count;
  const col = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) { col[i * 3] = c.r; col[i * 3 + 1] = c.g; col[i * 3 + 2] = c.b; }
  ng.setAttribute('color', new THREE.BufferAttribute(col, 3));
  return ng;
}
function box(w: number, h: number, d: number, x: number, y: number, z: number, hex: number, yaw = 0): THREE.BufferGeometry {
  const g = new THREE.BoxGeometry(w, h, d);
  if (yaw !== 0) g.rotateY(yaw);
  g.translate(x, y, z);
  return tinted(g, hex);
}
function cyl(r: number, h: number, x: number, y: number, z: number, hex: number, seg = 10, rTop = r): THREE.BufferGeometry {
  const g = new THREE.CylinderGeometry(rTop, r, h, seg);
  g.translate(x, y, z);
  return tinted(g, hex);
}
function torus(r: number, tube: number, y: number, hex: number): THREE.BufferGeometry {
  const g = new THREE.TorusGeometry(r, tube, 6, 28);
  g.rotateX(Math.PI / 2);
  g.translate(0, y, 0);
  return tinted(g, hex);
}
/** A stool centred on (x, z): the sitter's hips rest on its top. */
function stool(x: number, z: number): THREE.BufferGeometry[] {
  return [cyl(0.2, 0.06, x, SEAT_H - 0.03, z, WOOD, 10), cyl(0.05, SEAT_H - 0.06, x, (SEAT_H - 0.06) / 2, z, WOOD_DARK, 6)];
}

function partsFor(kind: string): THREE.BufferGeometry[] {
  const def = stationDef(kind);
  if (!def) return [];
  const n = def.capacity;
  const slots = Array.from({ length: n }, (_, i) => formationSlot(def.formation, i, n, def.spacing));
  const out: THREE.BufferGeometry[] = [];
  switch (def.prop) {
    case 'bench': {
      // One plank under the whole row, its top at seat height, centred on the sitters' hips.
      const len = def.spacing * n + 0.2;
      out.push(box(len, 0.06, 0.42, 0, SEAT_H - 0.03, -0.06, WOOD));
      out.push(box(len, 0.4, 0.05, 0, SEAT_H + 0.22, -0.28, WOOD));                       // the back rest
      for (const sx of [-len / 2 + 0.15, 0, len / 2 - 0.15]) out.push(box(0.08, SEAT_H - 0.06, 0.4, sx, (SEAT_H - 0.06) / 2, -0.06, WOOD_DARK));
      break;
    }
    case 'table': {
      const xs = slots.map((s) => s.x);
      const len = Math.max(...xs) - Math.min(...xs) + 1.0;
      out.push(box(len, 0.06, 0.8, 0, 0.78, 0, WOOD));
      for (const sx of [-len / 2 + 0.12, len / 2 - 0.12]) for (const sz of [-0.3, 0.3]) out.push(box(0.08, 0.75, 0.08, sx, 0.375, sz, WOOD_DARK));
      for (const s of slots) out.push(...stool(s.x, s.z));
      out.push(cyl(0.05, 0.12, -0.2, 0.87, 0.05, ALLOY, 8), cyl(0.05, 0.12, 0.25, 0.87, -0.1, ALLOY, 8)); // two mugs left on the board
      break;
    }
    case 'desk': {
      for (const s of slots) {
        out.push(...stool(s.x, s.z));
        out.push(box(1.0, 0.05, 0.55, s.x, 0.76, s.z + 0.5, WOOD));
        out.push(box(0.06, 0.74, 0.5, s.x - 0.45, 0.37, s.z + 0.5, WOOD_DARK), box(0.06, 0.74, 0.5, s.x + 0.45, 0.37, s.z + 0.5, WOOD_DARK));
        out.push(box(0.3, 0.03, 0.22, s.x, 0.8, s.z + 0.5, WHITE));                          // the open ledger
      }
      break;
    }
    case 'hearth': {
      // The fire the story circle faces: a ring of stones, crossed logs, an ember glow (vertex colour; the night adds the rest).
      for (let i = 0; i < 9; i++) { const a = (i / 9) * Math.PI * 2; out.push(box(0.26, 0.2, 0.2, Math.sin(a) * 0.62, 0.1, Math.cos(a) * 0.62, STONE, a)); }
      out.push(box(0.9, 0.12, 0.14, 0, 0.12, 0, WOOD_DARK, 0.5), box(0.9, 0.12, 0.14, 0, 0.2, 0, WOOD_DARK, -0.6));
      out.push(cyl(0.28, 0.22, 0, 0.3, 0, EMBER, 7, 0.04));
      break;
    }
    case 'fire_ring': {
      // THE FIRE CIRCLE (2026-09-24): a ring of stones round a laid fire, and a LOG SEAT under every slot — laid along
      // the circle's tangent, its top at SEAT_H — so a versona sitting fireside is always on a log (the never-mime law).
      for (let i = 0; i < 12; i++) { const a = (i / 12) * Math.PI * 2; out.push(box(0.3, 0.22, 0.24, Math.sin(a) * 0.78, 0.11, Math.cos(a) * 0.78, STONE, a)); }
      out.push(box(1.0, 0.14, 0.16, 0, 0.1, 0, WOOD_DARK, 0.5), box(1.0, 0.14, 0.16, 0, 0.18, 0, WOOD_DARK, -0.7), box(0.9, 0.12, 0.14, 0, 0.26, 0, WOOD_DARK, 1.9));
      // The bed of coals is NOT in the merged prop any more (fire realism R2, 2026-09-25): it is its own mesh
      // (`fireBedGeometry`, the same cylinder) wearing the fire's emissive coal shader, drawn by the fire system
      // wherever the fire burns — a tinted vertex colour cannot glow, crack or breathe with the flame.
      for (const s of slots) {
        const a = Math.atan2(s.x, s.z);
        const g = new THREE.CylinderGeometry(0.2, 0.2, 1.05, 9);
        g.rotateZ(Math.PI / 2);   // lying along x…
        g.rotateY(a);             // …turned onto the circle's tangent at this slot
        g.translate(s.x, SEAT_H - 0.2, s.z);
        out.push(tinted(g, WOOD));
      }
      break;
    }
    case 'ration_table': {
      out.push(box(2.2, 0.06, 0.8, 0, 0.8, 1.2, WOOD));
      for (const sx of [-1, 1]) out.push(box(0.08, 0.78, 0.7, sx, 0.39, 1.2, WOOD_DARK));
      for (const sx of [-0.7, 0, 0.7]) out.push(box(0.4, 0.3, 0.4, sx, 0.98, 1.2, STRAW));   // sacks of grain
      break;
    }
    case 'stall': {
      const len = def.spacing * n + 0.6;
      out.push(box(len, 0.06, 0.9, 0, 0.82, 1.1, WOOD));
      for (const sx of [-len / 2 + 0.1, len / 2 - 0.1]) { out.push(box(0.08, 2.3, 0.08, sx, 1.15, 0.7, WOOD_DARK), box(0.08, 2.3, 0.08, sx, 1.15, 1.5, WOOD_DARK)); }
      out.push(box(len + 0.3, 0.05, 1.3, 0, 2.32, 1.1, CLOTH));                              // the awning
      for (let i = 0; i < n; i++) out.push(box(0.34, 0.2, 0.34, slots[i].x, 0.95, 1.1, i % 2 ? RED : ALLOY)); // wares to pick up
      break;
    }
    case 'kneeler': for (const s of slots) out.push(box(0.5, 0.08, 0.4, s.x, 0.04, s.z, CLOTH, -s.face)); break;
    case 'mat': {
      out.push(cyl(3.2, 0.03, 0, 0.015, 1.4, STRAW, 24));
      out.push(box(0.9, 1.1, 0.05, 0, 1.0, -0.7, WOOD_DARK), box(0.8, 0.9, 0.03, 0, 1.0, -0.67, 0x2c3a33));    // the teacher's slate
      break;
    }
    case 'well_bucket': {
      out.push(cyl(0.2, 0.3, 0.5, 0.15, 0.6, WOOD, 10, 0.24));
      out.push(torus(0.23, 0.015, 0.3, ALLOY).translate(0.5, 0, 0.6) as THREE.BufferGeometry);
      break;
    }
    case 'targets': {
      for (const s of slots) {
        out.push(box(0.1, 1.5, 0.1, s.x, 0.75, 18, WOOD_DARK));
        const face = new THREE.CylinderGeometry(0.55, 0.55, 0.08, 18); face.rotateX(Math.PI / 2); face.translate(s.x, 1.45, 17.94);
        out.push(tinted(face, STRAW));
        const bull = new THREE.CylinderGeometry(0.16, 0.16, 0.1, 12); bull.rotateX(Math.PI / 2); bull.translate(s.x, 1.45, 17.9);
        out.push(tinted(bull, RED));
      }
      out.push(box(def.spacing * n + 0.6, 0.06, 0.12, 0, 0.03, 0.45, ROPE));                   // the firing line
      break;
    }
    case 'ring_rope': {
      const r = Math.max(def.spacing, (def.spacing * n) / (2 * Math.PI)) + 0.7;
      out.push(torus(r, 0.03, 0.9, ROPE), torus(r, 0.03, 0.5, ROPE));
      for (let i = 0; i < 6; i++) { const a = (i / 6) * Math.PI * 2; out.push(cyl(0.06, 1.0, Math.sin(a) * r, 0.5, Math.cos(a) * r, WOOD_DARK, 6)); }
      break;
    }
    case 'cot': {
      for (const s of slots) {
        out.push(box(0.8, 0.08, 1.9, s.x, 0.34, s.z, WOOD), box(0.76, 0.1, 1.84, s.x, 0.43, s.z, WHITE), box(0.5, 0.1, 0.34, s.x, 0.5, s.z - 0.7, CLOTH));
        for (const lx of [-0.34, 0.34]) for (const lz of [-0.85, 0.85]) out.push(box(0.06, 0.3, 0.06, s.x + lx, 0.15, s.z + lz, WOOD_DARK));
      }
      break;
    }
    default: break;
  }
  return out;
}

/** The fire ring's bed of coals (the cylinder the merged prop used to carry, EMBER-tinted): r 0.34 → 0.28, 0.12 tall, centred 0.08 up. */
export const FIRE_BED = { radius: 0.34, radiusTop: 0.28, height: 0.12, y: 0.08 } as const;
let bedGeo: THREE.BufferGeometry | null = null;
/** The bed's geometry (station-local: centred on the ring, y = 0 the ground) — the fire system's emissive coals. */
export function fireBedGeometry(): THREE.BufferGeometry {
  if (!bedGeo) {
    bedGeo = new THREE.CylinderGeometry(FIRE_BED.radiusTop, FIRE_BED.radius, FIRE_BED.height, 12);
    bedGeo.translate(0, FIRE_BED.y, 0);
    bedGeo.deleteAttribute('uv'); // the coal shader reads position (+ normal) only
    bedGeo.userData.shared = true;
  }
  return bedGeo;
}
/** The ember tint the bed wore as a vertex colour (kept for the harness and the sprite tier's cheap bed). */
export const FIRE_BED_TINT = EMBER;

const cache = new Map<string, THREE.BufferGeometry | null>();
let material: THREE.MeshStandardMaterial | null = null;

/** The merged geometry for a station kind (null = open ground: a green, a dance floor). Cached for the session. */
export function stationPropGeometry(kind: string): THREE.BufferGeometry | null {
  if (cache.has(kind)) return cache.get(kind) ?? null;
  const parts = partsFor(kind);
  const merged = parts.length > 0 ? mergeGeometries(parts, false) : null;
  for (const p of parts) p.dispose();
  if (merged) merged.computeBoundingSphere();
  cache.set(kind, merged);
  return merged;
}

/** One shared material for every station prop (vertex colours carry the look). */
export function stationPropMaterial(): THREE.MeshStandardMaterial {
  if (!material) material = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.85, metalness: 0.05 });
  return material;
}

/** A station's prop mesh, seated by the caller (position = the station on the ground, rotation.y = its facing). */
export function buildStationProp(kind: string): THREE.Mesh | null {
  const g = stationPropGeometry(kind);
  if (!g) return null;
  const mesh = new THREE.Mesh(g, stationPropMaterial());
  mesh.name = `station:${kind}`;
  mesh.castShadow = true; mesh.receiveShadow = true;
  mesh.matrixAutoUpdate = false;
  mesh.userData.sharedAsset = true; // the geometry and the material outlive every station (dispose skips them)
  return mesh;
}

/** Triangles a station kind draws (the harness pins the budget: a prop is set dressing, never a whale). */
export function stationPropTriangles(kind: string): number {
  const g = stationPropGeometry(kind);
  return g ? g.getAttribute('position').count / 3 : 0;
}
